import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes, { object, oneOf } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  NamedRedirect,
  FieldRadioButtonGroup,
  FieldCustomSelect,
  FieldCheckbox,
  InlineTextButton,
  FieldPhoneNumberInputWithDialCode,
} from '../../components';
import css from './ReferenceForm.module.css';
import {
  employmentType,
  languageSelectOptions,
  listingTypes,
  radioButtonType,
  workingStatus as workStatus,
} from '../../marketplace-custom-config';
import { isEmpty } from 'lodash';
import { ReactComponent as InfoIcon } from '../../assets/svg/infoIcon.svg';

import { Tooltip as ReactTooltip } from 'react-tooltip';
const nativeLangOptions = Object.keys(languageSelectOptions);
const textfield = 'textfield';
const errorTextField = 'errorTextField';

const FormComponent = props => {
  const {
    formName,
    formId,
    firstNameLabel,
    firstNamePlaceholder,
    firstNameValidator,
    lastNameLabel,
    lastNamePlaceholder,
    lastNameValidator,
    companyNameLabel,
    companyNamePlaceholder,
    companyNameValidator,
    jobTitleLabel,
    jobTitlePlaceholder,
    jobTitleValidator,
    emailLabel,
    emailPlaceholder,
    emailValidator,
    phoneNumberLabel,
    phoneNumberPlaceholder,
    phoneNumberValidator,
  } = props;

  return (
    <React.Fragment>
      <div className={css.twoFeildContainer}>
        <FieldTextInput
          className={textfield}
          errorClassName={errorTextField}
          type="text"
          id={formId ? `${formId}.firstName` : 'firstName'}
          name={formName ? `${formName}.firstName` : 'firstName'}
          label={firstNameLabel}
          placeholder={firstNamePlaceholder}
          validate={firstNameValidator}
        />
        <FieldTextInput
          className={textfield}
          errorClassName={errorTextField}
          type="text"
          id={formId ? `${formId}.lastName` : 'lastName'}
          name={formName ? `${formName}.lastName` : 'lastName'}
          label={lastNameLabel}
          placeholder={lastNamePlaceholder}
          validate={lastNameValidator}
        />
      </div>
      <div className={css.twoFeildContainer}>
        <FieldPhoneNumberInputWithDialCode
          className={textfield}
          errorClassName={errorTextField}
          id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
          name={formName ? `${formName}.phoneNumber` : 'phoneNumber'}
          label={phoneNumberLabel}
          placeholder={phoneNumberPlaceholder}
          validate={phoneNumberValidator}
        />

        <FieldTextInput
          className={textfield}
          errorClassName={errorTextField}
          type="email"
          id={formId ? `${formId}.email` : 'email'}
          name={formName ? `${formName}.email` : 'email'}
          label={emailLabel}
          placeholder={emailPlaceholder}
          validate={emailValidator}
        />
      </div>
      <div className={css.twoFeildContainer}>
        <FieldTextInput
          className={textfield}
          errorClassName={errorTextField}
          type="text"
          id={formId ? `${formId}.companyName` : 'companyName'}
          name={formName ? `${formName}.companyName` : 'companyName'}
          label={companyNameLabel}
          placeholder={companyNamePlaceholder}
          validate={companyNameValidator}
        />
        <FieldTextInput
          className={textfield}
          errorClassName={errorTextField}
          type="text"
          id={formId ? `${formId}.jobTitle` : 'jobTitle'}
          name={formName ? `${formName}.jobTitle` : 'jobTitle'}
          label={jobTitleLabel}
          placeholder={jobTitlePlaceholder}
          validate={jobTitleValidator}
        />
      </div>
    </React.Fragment>
  );
};

const ReferenceFormComponent = props => {
  const {
    onSubmit,
    useForm,
    intl,
    userNativeLang,
    formId,
    name: formName,
    values = {},
    formError,
    onCancel,
    ...restProps
  } = props;

  const formApi = useRef(null);
  const handleSubmit = values => {
    const { phoneNumber, ...restValues } = values;
    const { dialCode, iso2, iso3, number } = phoneNumber;
    const newPhoneNumber = { dialCode, iso2, iso3, number };
    const updatedValues = { ...restValues, phoneNumber: newPhoneNumber };
    onSubmit(updatedValues);
  };

  const { firstName, lastName, companyName, jobTitle, phoneNumber } = values;

  const submitDisabled =
    !firstName ||
    !lastName ||
    !companyName ||
    !jobTitle ||
    !phoneNumber?.number ||
    !isEmpty(formError);

  const handleKeyDown = event => {
    const isEnterKey = event.keyCode === 13 || event.key.toLowerCase() === 'enter';
    if (isEnterKey) {
      event.preventDefault();
    }
  };

  const handleSave = event => {
    event.preventDefault();
    event.stopPropagation();
    if (submitDisabled) return;
    handleSubmit({ ...values, isEditMode: false });
  };

  const handleCancel = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onCancel === 'function') onCancel(event);
  };

  //first name
  const firstNameLabel = intl.formatMessage({ id: 'ReferenceForm.firstNameLabel' });
  const firstNamePlaceholder = intl.formatMessage({
    id: 'ReferenceForm.firstNamePlaceholder',
  });
  const firstNameRequriedMessage = intl.formatMessage({ id: 'ReferenceForm.firstNameRequired' });
  const firstNameValidator = validators.required(firstNameRequriedMessage);

  // last name
  const lastNameLabel = intl.formatMessage({ id: 'ReferenceForm.lastNameLabel' });
  const lastNamePlaceholder = intl.formatMessage({
    id: 'ReferenceForm.lastNamePlaceholder',
  });
  const lastNameRequriedMessage = intl.formatMessage({ id: 'ReferenceForm.lastNameRequired' });
  const lastNameValidator = validators.required(lastNameRequriedMessage);

  //company name
  const companyNameLabel = intl.formatMessage({ id: 'ReferenceForm.companyNameLabel' });
  const companyNamePlaceholder = intl.formatMessage({
    id: 'ReferenceForm.companyNamePlaceholder',
  });
  const companyNameRequriedMessage = intl.formatMessage({
    id: 'ReferenceForm.companyNameRequired',
  });
  const companyNameValidator = validators.required(companyNameRequriedMessage);

  // job title
  const jobTitleLabel = intl.formatMessage({ id: 'ReferenceForm.jobTitleLabel' });
  const jobTitlePlaceholder = intl.formatMessage({
    id: 'ReferenceForm.jobTitlePlaceholder',
  });
  const jobTitleRequriedMessage = intl.formatMessage({ id: 'ReferenceForm.jobTitleRequired' });
  const jobTitleValidator = validators.required(jobTitleRequriedMessage);

  //email
  const emailLabel = intl.formatMessage({ id: 'ReferenceForm.emailLabel' });
  const emailPlaceholder = intl.formatMessage({
    id: 'ReferenceForm.emailPlaceholder',
  });
  const emailInvalidMessage = intl.formatMessage({ id: 'ReferenceForm.emailInvalid' });
  const emailValidator = validators.emailValueHasValidFormat(emailInvalidMessage);

  // phone number
  const phoneNumberLabel = intl.formatMessage({ id: 'ReferenceForm.phoneNumberLabel' });
  const phoneNumberPlaceholder = intl.formatMessage({
    id: 'ReferenceForm.phoneNumberPlaceholder',
  });
  const phoneNumberRequriedMessage = intl.formatMessage({
    id: 'ReferenceForm.phoneNumberRequired',
  });
  const phoneNumberValidator = validators.phoneNumberRequired(phoneNumberRequriedMessage);

  const formComponent = (
    <FormComponent
      formId={formId}
      formName={formName}
      firstNameLabel={firstNameLabel}
      firstNamePlaceholder={firstNamePlaceholder}
      firstNameValidator={firstNameValidator}
      lastNameLabel={lastNameLabel}
      lastNamePlaceholder={lastNamePlaceholder}
      lastNameValidator={lastNameValidator}
      companyNameLabel={companyNameLabel}
      companyNamePlaceholder={companyNamePlaceholder}
      companyNameValidator={companyNameValidator}
      jobTitleLabel={jobTitleLabel}
      jobTitlePlaceholder={jobTitlePlaceholder}
      jobTitleValidator={jobTitleValidator}
      emailLabel={emailLabel}
      emailPlaceholder={emailPlaceholder}
      emailValidator={emailValidator}
      phoneNumberLabel={phoneNumberLabel}
      phoneNumberPlaceholder={phoneNumberPlaceholder}
      phoneNumberValidator={phoneNumberValidator}
    />
  );

  return useForm ? (
    <FinalForm
      {...restProps}
      onSubmit={handleSubmit}
      intl={intl}
      userNativeLang={userNativeLang}
      formId={formId}
      formName={formName}
      onCancel={onCancel}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          inProgress,
          invalid,
          form,
          intl,
          onCancel,
          handleSubmit,
        } = formRenderProps;
        formApi.current = form;

        const submitDisabled = invalid || inProgress;

        const clasess = classNames(css.root || rootClassName, className);

        const handleCancel = e => {
          e.preventDefault();
          e.stopPropagation();
          form.restart();
          if (typeof onCancel === 'function') onCancel(e);
        };

        return (
          <Form className={clasess} onSubmit={handleSubmit}>
            {/* <div className={css.tooltipDiv}>
              <span id="ReferenceTooltip" className={css.icon}>
                <InfoIcon />
              </span>

              <ReactTooltip
                anchorId="ReferenceTooltip"
                place="top"
                effect="solid"
                clickable={false}
                className={css.categoryTooltip}
                content={<FormattedMessage id="FieldReference.subHeading" />}
              />
            </div> */}
            <div>{formComponent}</div>

            <div className={css.btncomp}>
              <PrimaryButton type="submit" disabled={submitDisabled} className={css.submitButton}>
                {intl.formatMessage({ id: 'ReferenceForm.saveRefBtn' })}
              </PrimaryButton>
              <InlineTextButton type="button" onClick={handleCancel}>
                {intl.formatMessage({ id: 'ReferenceForm.cancelRefBtn' })}
              </InlineTextButton>
            </div>
          </Form>
        );
      }}
    />
  ) : (
    <div onKeyDown={handleKeyDown}>
      <div>{formComponent}</div>
      <div className={css.btncomp}>
        <PrimaryButton
          type="submit"
          disabled={submitDisabled}
          onClick={handleSave}
          className={css.submitButton}
        >
          {intl.formatMessage({ id: 'ReferenceForm.saveRefBtn' })}
        </PrimaryButton>
        <InlineTextButton type="button" onClick={handleCancel}>
          {intl.formatMessage({ id: 'ReferenceForm.cancelRefBtn' })}
        </InlineTextButton>
      </div>
    </div>
  );
};

ReferenceFormComponent.defaultProps = {
  inProgress: false,
  userNativeLang: languageSelectOptions.en.value,
  useForm: false,
  values: {},
  formError: {},
};

const { bool, func } = PropTypes;

ReferenceFormComponent.propTypes = {
  inProgress: bool,

  // from injectIntl
  intl: intlShape.isRequired,
  userNativeLang: oneOf(nativeLangOptions).isRequired,
  onCancel: func.isRequired,
  useForm: bool.isRequired,
  values: object.isRequired,
  formError: object.isRequired,
};

const ReferenceForm = compose(injectIntl)(ReferenceFormComponent);
ReferenceForm.displayName = 'ReferenceForm';

export default ReferenceForm;
